import type { Quote } from '@/types/v2/quote';
import type { Request } from '@/types/v2/request';
import axios from '@/lib/v2/axios';

export async function getRequest(requestId?: string): Promise<Request> {
  const response = await axios.get(`/api/v2/requests/${requestId}`);
  return response.data;
}

export async function getRequestsByCommunityId(
  communityId?: string,
  params?: { params: { include?: string; sort?: string } }
): Promise<{ requests: Request[] }> {
  if (!communityId) {
    return { requests: [] };
  }

  const response = await axios.get(`/api/v2/communities/${communityId}/requests`, {
    ...params,
  });
  
  // Handle both old paginated and new non-paginated response formats
  if (response.data.requests && Array.isArray(response.data.requests)) {
    return response.data;
  }
  
  // If the response is directly an array (non-paginated), wrap it
  if (Array.isArray(response.data)) {
    return { requests: response.data };
  }
  
  // Fallback to empty array
  return { requests: [] };
}

export async function getRequestByCommunityId(
  communityId?: string,
  requestId?: string,
  params?: { params: { include?: string; sort?: string } }
): Promise<{ request: Request } | null> {
  if (!communityId) {
    return null;
  }

  try {
    const response = await axios.get(`/api/v2/communities/${communityId}/requests/${requestId}`, {
      ...params,
    });
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error('Request not found');
    }
    throw error;
  }
}

export async function getRequestQuotesByCommunityId(
  communityId?: string,
  requestId?: string,
  params?: { params: { include?: string; sort?: string } }
): Promise<{ quotes: Quote[] }> {
  if (!communityId) {
    return { quotes: [] };
  }

  try {
    const response = await axios.get(`/api/v2/communities/${communityId}/requests/${requestId}/quotes`, {
      ...params,
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching request quotes:', error);
    return { quotes: [] }; // Return an empty array or handle the error as needed
  }
}

export async function deleteRequest(
  communityId?: string,
  requestId?: string
): Promise<void> {
  if (!communityId || !requestId) {
    throw new Error('Community ID and Request ID are required');
  }

  await axios.delete(`/api/v2/communities/${communityId}/requests/${requestId}`);
}

export async function getRequestsByUnit(communityId: string, unitId: string): Promise<Request[]> {
  const response = await axios.get(`/api/v2/communities/${communityId}/requests?filter[unit_id]=${unitId}`, {
    params: {
      include: 'requestType,requestStatus,unit.owners,unit.currentResidents,unit.unitType,quotes,assignedTo,createdBy',
    },
  });
  return response.data.requests || [];
}
